/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, {
    useEffect, useRef, useState,
} from 'react';
import {
    any, object, func, string,
} from 'prop-types';
import mbpUtil from '@api-util/mbp';
import mbpLogger from '@module-logger/mbp';
import { useSelector, useDispatch } from 'react-redux';
import { getAccessToken } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getAccessTokenSafely } from '../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { trackEvent } from '../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import ConversationalQualtricsFeedBackContainer from './ConversationalUIFeedbackContainer';
import getTrustedOrigin from './helper/TrustedOrigin';

const FLORA_MESSAGE_TYPES = new Set([
    'flora-conv-ui-session-info',
    'flora-search-suggestion-submitted',
    'flora-footer-suggestion-submitted',
    'flora-get-access-token',
    'flora-get-page-info',
    'flora-navigateProductPage',
    'flora-chat-iframe-maximised',
    'flora-chat-iframe-minimised',
    'flora-chat-preview-msg-closed',
    'flora-preview-msg-ready',
    'flora-no-preview-msg',
    'flora-end-chat-hide-button',
    'flora-chat-trigger-event-tracking',
]);

const getIframeContentWindow = (iframeId) => document.getElementById(iframeId)?.contentWindow;

const isExpectedFloraMessageSource = (event, type, floraContentWindow) => {
    if (event.source === floraContentWindow) return true;

    if (type === 'flora-search-suggestion-submitted') {
        return event.source === getIframeContentWindow('18fchatbotsearch');
    }

    if (type === 'flora-footer-suggestion-submitted') {
        return event.source === getIframeContentWindow('18fchatbotfooter');
    }

    return false;
};

const ConversationalUIChatBot = ({
    deviceType, brand, browser, operatingSystem, brandTheme, pageType, location, setIsFloraChatBtnClicked, featureFlags,
}) => {
    const iframeRef = useRef(null);
    const pageTypeRef = useRef(pageType);
    const [isIframeMaximized, setIsIframeMaximized] = useState(false);
    const isMobile = deviceType !== 'desktop';
    const [isIframeLoaded, setIsIframeLoaded] = useState(true);
    const trustedOrigin = getTrustedOrigin(mbpUtil?.getEnv('APP_CONVERSATIONAL_UI_HOST'));
    const cartItems = useSelector((state) => state?.cart?.recipients);
    const cartHasPassport = useRef('false');
    const orderDetails = useSelector((state) => state?.cart);
    const ORDER_PLACED = 'Published';
    const dispatch = useDispatch();
    const [orderSize, setOrderSize] = useState(0);
    const [currentLocation, setCurrentLocation] = useState(undefined);
    const accessToken = useSelector(getAccessToken);
    const isFloraChatQualtricsSurveyButtonEnabled = featureFlags['is-flora-chat-persistent-qualtrics-survey-button-enabled'];
    const [feedbackMinimized, setFeedbackMinimized] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [isFeedbackButtonDisplayed, setIsFeedbackButtonDisplayed] = useState(isFloraChatQualtricsSurveyButtonEnabled);
    const [isChatDisplayed, setIsChatDisplayed] = useState(!isMobile);

    const getSessionData = (token) => {
        const url = window.location.href;
        const browseruuid = localStorage?.getItem('browserUUID') || '';

        if (brand?.code === 'CSG' || brand?.version === '5.0') {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

            return {
                url,
                brand,
                browser,
                deviceType,
                operatingSystem,
                brandTheme,
                auth: { isAuthenticated: !!userInfo?.profile?.email },
                profile: userInfo?.profile,
                enterprise: { enterpriseId: '' },
                browseruuid,
                token: userInfo?.token || token || '',
            };
        }

        const memberSession = JSON.parse(sessionStorage.getItem('persist:member') || '{}');
        const auth = JSON.parse(memberSession?.auth || '{}');
        const profile = JSON.parse(memberSession?.profile || '{}');
        const enterprise = JSON.parse(memberSession?.enterpriseId || '{}');

        return {
            url,
            brand,
            browser,
            deviceType,
            operatingSystem,
            brandTheme,
            auth,
            profile,
            enterprise,
            browseruuid,
            token,
        };
    };

    const getFloraAccessToken = (token) => {
        if (brand?.code === 'CSG' || brand?.version === '5.0') {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            return userInfo?.token || token || '';
        }

        return token;
    };

    const resizeIframeForMaximizedChat = () => {
        if (!iframeRef.current) return;

        if (isMobile) {
            setIsIframeMaximized(() => true);
            iframeRef.current.style.top = '0';
            iframeRef.current.style.height = '100dvh';
            iframeRef.current.style.width = '100vw';
            iframeRef.current.style.right = '';
            iframeRef.current.style.bottom = '';
            return;
        }

        const screenWidth = window.innerWidth;
        if (screenWidth < 1024) {
            iframeRef.current.style.width = '45%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-7%';
            iframeRef.current.style.right = '0%';
        } else if (screenWidth >= 1024 && screenWidth <= 1280) {
            iframeRef.current.style.width = '40%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-6%';
            iframeRef.current.style.right = '0%';
        } else if (screenWidth > 1280 && screenWidth <= 1440) {
            iframeRef.current.style.width = '32%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-4%';
            iframeRef.current.style.right = '0%';
        } else if (screenWidth > 1440 && screenWidth <= 1920) {
            iframeRef.current.style.width = '30%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-4%';
            iframeRef.current.style.right = '2%';
        } else if (screenWidth > 1920) {
            iframeRef.current.style.width = '20%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-5%';
            iframeRef.current.style.right = '1%';
        }

        if (isFloraChatQualtricsSurveyButtonEnabled) {
            setIsFeedbackButtonDisplayed(false);
            if (feedbackOpen === true) setFeedbackMinimized(!feedbackMinimized);
            setFeedbackOpen(false);
        }
    };

    const resizeIframeForMinimizedChat = () => {
        if (!iframeRef.current) return;

        setIsIframeMaximized(() => false);
        iframeRef.current.style.width = '120px';
        iframeRef.current.style.height = '80px';
        iframeRef.current.style.top = '';
        iframeRef.current.style.bottom = '10px';
        iframeRef.current.style.right = '0%';
        if (isFloraChatQualtricsSurveyButtonEnabled && !isMobile) {
            setIsFeedbackButtonDisplayed(true);
        }
    };

    useEffect(() => {
        if (isIframeMaximized) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [isIframeMaximized]);

    useEffect(() => {
        if (accessToken) {
            try {
                const auth = JSON.parse(JSON.parse(sessionStorage.getItem('persist:member'))?.auth);
                const iframe = iframeRef.current;
                if (iframe && iframe?.contentWindow) {
                    iframe.contentWindow.postMessage(
                        { type: 'flora-update-access-token', data: auth },
                        `${trustedOrigin}/chat/flora`,
                    );
                }
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    errorMessage: error,
                    message: `FloraChat: Token Issue - ERROR: ${error}`,
                });
            }
        }
    }, [accessToken]);

    useEffect(() => {
        cartItems?.forEach((cartItem) => {
            cartItem?.cartItems?.forEach((item) => {
                if (item?.itemInfo?.lineItemType === 'PASSPORT') {
                    cartHasPassport.current = 'true';
                }
            });
        });
        const iframe = iframeRef.current;
        if (iframe && iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                { type: 'flora-update-cart-passport-status', data: { cartHasPassport: cartHasPassport.current } },
                `${trustedOrigin}/chat/flora`,
            );
        }
    }, [cartItems]);

    useEffect(() => {
        pageTypeRef.current = pageType;
        const iframe = iframeRef.current;
        if (iframe && iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                { type: 'flora-update-page-info', data: pageTypeRef.current }, `${trustedOrigin}/chat/flora`,
            );
        }
    }, [pageType]);

    /**
         * send current url to the connected agent whenever the user change the location
         */
    useEffect(() => {
        const path = location?.pathname;
        if (currentLocation !== path) {
            setCurrentLocation(window?.location?.href);
            const customParameters = {
                currentUrl: window?.location?.href,
            };
            const iframe = iframeRef.current;
            if (iframe && iframe.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'flora-update-visiting-page', data: customParameters },
                    `${trustedOrigin}/chat/flora`,
                );
            }
        }
    }, [location?.pathname]);

    /**
     * send order details to the connected agent whenever the user add or remove something.
     */
    useEffect(() => {
        if (orderDetails && orderDetails.orderSize !== orderSize) {
            const itemAdded = orderDetails.orderSize > orderSize;
            setOrderSize(orderDetails.orderSize);
            const customParameters = {
                isItemAdded: itemAdded,
                orderValue: orderDetails.cartTotal,
                orderNumber: orderDetails.orderIdWithSourceId,
            };
            const iframe = iframeRef.current;
            if (iframe && iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'flora-update-cart-details', data: customParameters },
                    `${trustedOrigin}/chat/flora`,
                );
            }
        }
    }, [orderDetails]);

    useEffect(() => {
        if (orderDetails?.cartStatus && orderDetails?.cartStatus === ORDER_PLACED) {
            const customParameters = {
                wasOrderPlaced: 'true',
                orderNumber: orderDetails?.cartId,
            };
            const iframe = iframeRef.current;
            if (iframe && iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'flora-update-order-placed-status', data: customParameters },
                    `${trustedOrigin}/chat/flora`,
                );
            }
        }
    }, [orderDetails?.cartStatus]);

    useEffect(() => {
        const handleMessage = async (event) => {
            try {
                const iframe = iframeRef.current;
                const type = event?.data?.type;

                if (
                    !iframe?.contentWindow
                    || event.origin !== trustedOrigin
                    || typeof type !== 'string'
                    || !FLORA_MESSAGE_TYPES.has(type)
                    || !isExpectedFloraMessageSource(event, type, iframe.contentWindow)
                ) {
                    return;
                }

                let token = accessToken;
                const env = (mbpUtil.getEnv('APP_GRAPHQL_ENV') || '')?.toLowerCase();
                if (env && !env.includes('prod')) {
                    token = await getAccessTokenSafely();
                }

                if (type === 'flora-conv-ui-session-info') {
                    event.source.postMessage({
                        type: 'flora-session-storage-data',
                        data: getSessionData(token),
                    }, `${trustedOrigin}/chat/flora`);
                }
                if (type === 'flora-search-suggestion-submitted') {
                    iframe.contentWindow.postMessage(
                        { type: 'flora-update-chat-message-search', data: event.data },
                        `${trustedOrigin}/chat/flora`,
                    );
                }
                if (type === 'flora-footer-suggestion-submitted') {
                    iframe.contentWindow.postMessage(
                        { type: 'flora-update-chat-message-footer', data: event.data },
                        `${trustedOrigin}/chat/flora`,
                    );
                }
                if (type === 'flora-get-access-token') {
                    iframe.contentWindow.postMessage(
                        { type: 'flora-update-access-token', data: getFloraAccessToken(token) },
                        `${trustedOrigin}/chat/flora`,
                    );
                }
                if (type === 'flora-get-page-info') {
                    setIsFloraChatBtnClicked(true);
                    iframe.contentWindow.postMessage(
                        { type: 'flora-update-page-info', data: pageTypeRef.current },
                        `${trustedOrigin}/chat/flora`,
                    );
                }
                if (type === 'flora-navigateProductPage') {
                    window.location.href = event.data.url;
                }
                if (type === 'flora-chat-iframe-maximised') {
                    if (isMobile && !isChatDisplayed) {
                        setIsChatDisplayed(true);
                    }
                    resizeIframeForMaximizedChat();
                }
                if (type === 'flora-chat-iframe-minimised') {
                    resizeIframeForMinimizedChat();
                }
                if (type === 'flora-chat-preview-msg-closed' || type === 'flora-no-preview-msg') {
                    iframeRef.current.style.width = '120px';
                    iframeRef.current.style.height = '80px';
                    iframeRef.current.style.right = '0%';
                }
                if (type === 'flora-preview-msg-ready') {
                    iframeRef.current.style.width = '350px';
                    iframeRef.current.style.height = '150px';
                }
                if (type === 'flora-end-chat-hide-button' && isMobile) {
                    setIsChatDisplayed(false);
                }
                if (type === 'flora-chat-trigger-event-tracking') {
                    const trackingData = event?.data?.tracking_data;
                    if (trackingData) {
                        dispatch(trackEvent(trackingData));
                    }
                }
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    errorMessage: error,
                    message: `FloraChat: Could not post value - ERROR: ${error}`,
                });
            }
        };
        if (isIframeLoaded) {
            window?.addEventListener('message', handleMessage);
        }
        return () => {
            window?.removeEventListener('message', handleMessage);
        };
    }, [isIframeLoaded, trustedOrigin]);

    const handleIframeLoad = () => {
        setIsIframeLoaded(true);
        dispatch(trackEvent({
            eventCategory: '18FChatBot',
            eventAction: 'Chat Loaded',
            eventLabel: '18FChatBot',
            interaction_type: 'click',
            ga_event_name: 'chat_interaction',
        }));
    };
    const handleIframeError = () => setIsIframeLoaded(false);

    return isIframeLoaded ? (
        <>
            <ConversationalQualtricsFeedBackContainer featureFlags={featureFlags} isMobile={isMobile} brand={brand} feedbackMinimized={feedbackMinimized} setFeedbackMinimized={setFeedbackMinimized} feedbackOpen={feedbackOpen} setFeedbackOpen={setFeedbackOpen} isFeedbackButtonDisplayed={isFeedbackButtonDisplayed} />
            <iframe
                id="18fchatbot"
                ref={iframeRef}
                title="18fchatbot"
                allowFullScreen
                style={{
                    overflow: 'hidden',
                    boxShadow: 'none',
                    border: 'none',
                    position: 'fixed',
                    zIndex: 2243000,
                    bottom: '10px',
                    right: '0%',
                    width: '120px',
                    height: '80px',
                    display: isChatDisplayed ? '' : 'none',
                }}
                src={`${trustedOrigin}/chat/flora`}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
            />
        </>
    ) : null;
};

ConversationalUIChatBot.propTypes = {
    deviceType: string.isRequired,
    brand: object.isRequired,
    browser: func.isRequired,
    operatingSystem: func.isRequired,
    brandTheme: any.isRequired,
    pageType: string.isRequired,
    location: object.isRequired,
    setIsFloraChatBtnClicked: func.isRequired,
    featureFlags: object.isRequired,
};
export default ConversationalUIChatBot;
